import { useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Flex } from '@semcore/ui/flex-box';
import SelectUI, { InputSearch } from '@semcore/ui/select';

import Cross from './components/icons/Cross';

import s from './Select.module.css';

const DEFAULT_PLACEHOLDER = 'Select option';

export interface SelectOption {
  label: string;
  value: string | number;
  labelCaption?: React.ReactNode;
}

export interface ISelectBaseProps {
  options: Readonly<SelectOption[]>;
  className?: string;
  onTop?: boolean;
  withSearch?: boolean;
  disabled?: boolean;
}

export interface ISelectSingleProps extends ISelectBaseProps {
  current?: SelectOption;
  placeholder?: string;
  onChange(data: SelectOption): void;
}

export interface ISelectMultiProps extends ISelectBaseProps {
  current?: SelectOption[];
  multiplyLabel?: string;
  onChange(data: SelectOption[]): void;
}

export type ISelectProps =
  | ({ type?: 'single' } & ISelectSingleProps)
  | ({ type: 'multi' } & ISelectMultiProps);

function SelectOne({
  options,
  placeholder = DEFAULT_PLACEHOLDER,
  current: currentValue,
  onTop = false,
  withSearch,
  className = '',
  onChange,
  disabled = false,
}: ISelectSingleProps) {
  const current = currentValue;
  const isActive = useCallback(
    (option: SelectOption) => current?.value === option.value,
    [current]
  );

  const [filter, setFilter] = useState('');
  const filteredOptions = useMemo(
    () =>
      filter
        ? options.filter((option) =>
            option.label.toLowerCase().includes(filter.toLowerCase())
          )
        : options,
    [filter, options]
  );

  const [visible, setVisible] = useState(false);

  const toggleVisible = () => {
    setVisible(!visible);
  };

  const withPlaceholder = placeholder && placeholder !== DEFAULT_PLACEHOLDER;

  // @ts-ignore
  return (
    <SelectUI
      value={current?.value}
      placeholder={placeholder}
      onChange={(value: SelectOption['value']) =>
        onChange(
          options.find((option) => option.value === value) as SelectOption
        )
      }
      visible={visible}
      onVisibleChange={toggleVisible}
      disabled={disabled}
    >
      <SelectUI.Trigger
        className={twMerge(
          '!h-auto !w-full !overflow-visible !rounded-lg !border !border-[#E8EAF0] !bg-default !px-2 !py-1 !text-[16px] !font-light !leading-[20px] dark:!border-[#FFFFFF1F]',
          className
        )}
        data-select="single"
      >
        {withPlaceholder && !current && (
          <div
            className={twMerge(
              'absolute top-2 text-xs font-semibold text-primary transition-all',
              visible && 'text-secondary'
            )}
          >
            {placeholder}
          </div>
        )}
        <div>{current?.label}</div>
      </SelectUI.Trigger>

      <SelectUI.Popper
        className={twMerge('!mt-1 !rounded-lg !bg-default !p-2', s.shadow)}
        placement={onTop ? 'top-start' : 'bottom-start'}
      >
        {({ highlightedIndex }) => (
          <>
            {withSearch && (
              // @ts-ignore
              <InputSearch
                value={filter}
                onChange={setFilter}
                // @ts-expect-error
                placeholder="Search"
                role="combobox"
                aria-autocomplete="list"
                aria-controls="search-list"
                aria-owns="search-list"
                aria-expanded="true"
                aria-activedescendant={`option-${highlightedIndex}`}
              />
            )}
            <SelectUI.List>
              {filteredOptions.map((option, index) => (
                <SelectUI.Option
                  className={twMerge(
                    'rounded-lg !px-3 !py-2 !text-[16px] !font-light !leading-[20px] !text-primary !shadow-none transition-all duration-300 ease-out hover:!bg-blue hover:!text-white dark:hover:!bg-[#FFFFFF14]',
                    isActive(option) && '!bg-[#F6F8FF] dark:!bg-[#FFFFFF1F]'
                  )}
                  value={option.value}
                  key={option.value}
                  id={`option-${index}`}
                  aria-selected={index === highlightedIndex}
                >
                  {option.labelCaption || option.label}
                </SelectUI.Option>
              ))}
              {!filteredOptions.length && (
                <SelectUI.OptionHint key="Nothing">No data</SelectUI.OptionHint>
              )}
            </SelectUI.List>
          </>
        )}
      </SelectUI.Popper>
    </SelectUI>
  );
}

function SelectMulti({
  options,
  current: currentValue,
  multiplyLabel = '',
  className = '',
  onTop = false,
  withSearch,
  onChange,
  disabled = false,
}: ISelectMultiProps) {
  const current = currentValue as SelectOption[];
  const optionValue = current.map((option) => option.value);

  let text: string;
  if ((current as SelectOption[]).length > 0) {
    text = `${multiplyLabel} chosen: ${current.length}`;
  } else {
    text = `All ${multiplyLabel}`;
  }

  const [filter, setFilter] = useState('');
  const filteredOptions = useMemo(
    () =>
      filter
        ? options.filter((option) =>
            option.label.toLowerCase().includes(filter.toLowerCase())
          )
        : options,
    [filter, options]
  );

  return (
    <SelectUI
      placeholder={text}
      disabled={disabled}
      value={optionValue}
      multiselect
      onChange={(value: SelectOption['value'][]) =>
        onChange(
          options.filter((option) =>
            value.includes(option.value)
          ) as SelectOption[]
        )
      }
    >
      <SelectUI.Trigger
        className={`!h-auto !w-full !rounded-lg !border !border-[#E8EAF0] !bg-default !px-2 !py-1 !text-[16px] !font-light !leading-[20px] hover:!fill-black dark:!border-[#FFFFFF1F] dark:hover:!fill-white ${className}`}
        onClick={(e) => {
          // @ts-ignore
          return !e.target?.dataset.close;
        }}
        data-select="multi"
      >
        <SelectUI.Trigger.Text className="!text-primary">
          {text}
        </SelectUI.Trigger.Text>
        <SelectUI.Trigger.Addon>
          {(current as SelectOption[]).length > 0 && (
            <Cross
              className="hover:stroke-black dark:hover:stroke-white"
              data-close
              onClick={() => onChange([])}
            />
          )}
        </SelectUI.Trigger.Addon>
      </SelectUI.Trigger>
      <SelectUI.Popper
        placement={onTop ? 'top-start' : 'bottom-start'}
        className={`!mt-1 !rounded-lg !bg-default !p-2 ${s.shadow}`}
      >
        {({ highlightedIndex }) => (
          <>
            {withSearch && (
              // @ts-ignore
              <InputSearch
                value={filter}
                onChange={setFilter}
                // @ts-expect-error
                placeholder="Search"
                role="combobox"
                aria-autocomplete="list"
                aria-controls="search-list"
                aria-owns="search-list"
                aria-expanded="true"
                aria-activedescendant={`option-${highlightedIndex}`}
              />
            )}
            <SelectUI.List>
              {filteredOptions.map((option, index) => (
                <SelectUI.Option
                  className="rounded-lg !bg-transparent !px-3 !py-2 !text-[16px] !font-light !leading-[20px] !text-primary !shadow-none transition-all duration-300 ease-out hover:!bg-zinc dark:hover:!bg-[#FFFFFF14]"
                  value={option.value}
                  id={`option-${index}`}
                  aria-selected={index === highlightedIndex}
                  key={option.value}
                >
                  <Flex alignItems="center">
                    <SelectUI.Option.Checkbox m="0 8px 0 0" />
                    <span className="!text-[16px] !font-light !leading-[20px]">
                      {option.labelCaption || option.label}
                    </span>
                  </Flex>
                </SelectUI.Option>
              ))}
              {!filteredOptions.length && (
                <SelectUI.OptionHint key="Nothing">No data</SelectUI.OptionHint>
              )}
            </SelectUI.List>
          </>
        )}
      </SelectUI.Popper>
    </SelectUI>
  );
}

export default function Select(props: ISelectProps) {
  const { type = 'single', ...rest } = props;

  return type === 'multi' ? (
    <SelectMulti {...(rest as ISelectMultiProps)} />
  ) : (
    <SelectOne {...(rest as ISelectSingleProps)} />
  );
}
